import {fileRenamedOnly} from '@github-ui/diff-file-helpers'
import {Diff, type DiffProps} from '@github-ui/diff-lines'
import {removeUrlHash} from '@github-ui/history'
import {Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {ContextLineRange} from '../page-data/loaders/use-context-lines-data'
import {
  useAnnotations,
  useDiffSummary,
  useCollapsedDiffStatus,
  useCollapsedDiffStatusActions,
  useThreads,
  useIsCommentBatchPending,
  useDiffEntryActions,
} from '../stores/selectors'
import {CodeownersBadge} from './CodeownersBadge'
import {BlobActionsMenu} from './BlobActionsMenu'
import {MarkAsViewedButton} from './MarkAsViewedButton'
import {usePullRequestCommenting} from '../hooks/use-pull-request-commenting'
import {extractAnnotationMarkerId, extractCommentThreadMarkerId, findMarkersByPosition} from '../helpers/diff-helpers'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useExtractMarkerIds} from '../hooks/use-extract-marker-ids'
import {STICKY_HEADER_HEIGHT} from './toolbar/PullRequestFilesToolbar'
import type {DiffLine, PullRequestState} from '@github-ui/diff-lines/types'
import type {RepoSubject} from '@github-ui/comment-box/subject'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import type {PullRequestAppPayload} from '../page-data/payloads/pull-request-app'
import {getSelectedFullDiffHash, parseAnnotationHash, parseCommentHash} from '@github-ui/diffs/document-hash-helpers'
import {IconButton} from '@primer/react'
import {CommentIcon} from '@primer/octicons-react'
import {useRichDiffQueryConfig} from '../page-data/loaders/use-rich-diff-page-data'

export type PullRequestDiffProps = Omit<
  DiffProps,
  | 'addInjectedContextLines'
  | 'leftSideContent'
  | 'rightSideContent'
  | 'onToggleCollapse'
  | 'commentingImplementation'
  | 'initialExpandedMarkerId'
  | 'fileComments'
  | 'isAddingFileComment'
  | 'fileComments'
  | 'setIsAddingFileComment'
  | 'collapsed'
> & {
  diffLines: DiffLine[]
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  isSelected?: boolean
  pullRequestState: PullRequestState
  commentBoxSubject?: RepoSubject
}

// Access data store, maps threads into the difflines
export function PullRequestDiff({
  basePath,
  diffManuallyExpanded,
  headBranchName,
  headRepoName,
  headRepoOwner,
  contextLinesURL,
  diffLines,
  path,
  commentBoxSubject,
  isSelected = false,
  reviewed,
  ...diff
}: PullRequestDiffProps) {
  const {fetchContextLines} = useDiffEntryActions()
  const richDiffQueryConfig = useRichDiffQueryConfig({
    basePath,
    path,
  })
  const diffRef = useRef<HTMLDivElement>(null)
  const addFileCommentButtonRef = useRef<HTMLButtonElement>(null)
  const {helpUrl} = useAppPayload<PullRequestAppPayload>()

  const commentBatchPending = useIsCommentBatchPending()
  const isCollapsed = useCollapsedDiffStatus(path)
  const {updateAllFilesCollapsedStatus, updateFileCollapsedStatus} = useCollapsedDiffStatusActions()

  const [initialExpandedMarkerId, setInitialExpandedMarkerId] = useState<string | undefined>(undefined)
  const [isAddingFileComment, setIsAddingFileComment] = useState(false)

  const diffSummary = useDiffSummary(path)

  const {threadIDs, annotationIDs} = useExtractMarkerIds(diffSummary)

  const annotations = useAnnotations(annotationIDs)
  const threads = useThreads(threadIDs)

  const diffLinesWithThreads = useMemo(() => {
    let tempDiffLinesWithThreads = diffLines ?? []
    let markersData = undefined
    if (annotations || threads) {
      markersData = {annotations: annotations ?? {}, threads: threads ?? {}}
    }
    if (diffSummary && markersData) {
      tempDiffLinesWithThreads = tempDiffLinesWithThreads.map(diffLine => {
        // find marker by position and merge it into the diffLine
        return {
          ...diffLine,
          ...findMarkersByPosition(diffLine, diffSummary, markersData),
        }
      })
    }
    return tempDiffLinesWithThreads
  }, [diffLines, annotations, threads, diffSummary])

  const fileComments = useMemo(() => {
    if (!threads) return []

    return Object.values(threads).filter(thread => thread && thread.subjectType?.toUpperCase() === 'FILE')
  }, [threads])

  const expandDiff = useCallback(() => {
    updateFileCollapsedStatus(path, false)
  }, [path, updateFileCollapsedStatus])

  /**
   * HASH LOGIC
   *
   * Setting this value ensures that when navigating directly to a marker (comment or annotation) via a URL hash,
   * the thread containing that comment is expanded, even when the user is in "Comments Minimized" mode.
   * Works in tandem with the `DiffLineTableCellParts` logic to enter dialog mode and the `ReviewThreadComment`
   *  to focus the comment.
   *
   * The value changes whenever the URL hash changes.
   */
  const updateExpandedMarkerFromUrlHash = useCallback(() => {
    const parsedCommentId = parseCommentHash(ssrSafeWindow?.location.hash ?? '')
    const parsedAnnotationId = parseAnnotationHash(ssrSafeWindow?.location.hash ?? '')

    let expandedMarkerId: string | undefined = undefined

    if (parsedCommentId) {
      expandedMarkerId = extractCommentThreadMarkerId(parsedCommentId, threads)
    } else if (parsedAnnotationId) {
      expandedMarkerId = extractAnnotationMarkerId(parsedAnnotationId, annotations)
    }

    if (expandedMarkerId && isCollapsed) {
      expandDiff()
    }

    // If we didn't find a matching thread or annotation, clear any previous state.
    setInitialExpandedMarkerId(expandedMarkerId)
  }, [isCollapsed, threads, annotations, expandDiff])

  const clearUrlHash = useCallback(() => {
    removeUrlHash()
    updateExpandedMarkerFromUrlHash()
  }, [updateExpandedMarkerFromUrlHash])

  /**
   * Expand on hash change logic.
   *
   * This logic is used to expand the diff when the hash changes to a specific diff entry.
   * This ensures that navigation to a specific diff entry via URL hash always expands the diff.
   * Scenarios include navigation from the file tree, comments, or other parts of the UI.
   */
  const expandDiffWhenSelectedByHash = useCallback(() => {
    const diffHash = getSelectedFullDiffHash()

    if (diffHash === diff.pathDigest && isCollapsed) {
      expandDiff()
    }
  }, [diff.pathDigest, expandDiff, isCollapsed])

  // consolidated hash change handler to reduce number of event listeners
  const handleHashChanges = useCallback(() => {
    updateExpandedMarkerFromUrlHash()
    expandDiffWhenSelectedByHash()
  }, [updateExpandedMarkerFromUrlHash, expandDiffWhenSelectedByHash])

  useEffect(() => {
    handleHashChanges()
    // eslint-disable-next-line react-hooks/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ssrSafeWindow?.addEventListener('hashchange', handleHashChanges)

    return () => {
      ssrSafeWindow?.removeEventListener('hashchange', handleHashChanges)
    }
  }, [handleHashChanges])
  /**
   * END HASH LOGIC
   */

  const commentingImplementation = usePullRequestCommenting(
    basePath,
    diff.newCommitOid ?? '',
    diff.currentUser.login,
    commentBoxSubject,
  )

  const onToggleCollapse = useCallback(
    (willCollapse: boolean, filePath: string, event?: React.MouseEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event && event.altKey) {
        updateAllFilesCollapsedStatus(willCollapse)
      } else {
        updateFileCollapsedStatus(filePath, willCollapse)
        if (!willCollapse) return
        ssrSafeWindow?.requestAnimationFrame(() => {
          const rect = diffRef.current?.getBoundingClientRect()

          if (!rect) return

          // Only the top boundary is checked to ensure the element is not obscured by the sticky header or scrolled
          // out of view for when collapsed while sticky. We don't need to check the bottom because all we care about
          // is the diff header being visible
          const isOutOfView = rect.top < STICKY_HEADER_HEIGHT

          if (!isOutOfView) return
          ssrSafeWindow?.scrollTo({
            top: rect.top + ssrSafeWindow.scrollY - STICKY_HEADER_HEIGHT,
          })
        })
      }
    },
    [updateAllFilesCollapsedStatus, updateFileCollapsedStatus],
  )
  const expandedRangesArray = useRef<ContextLineRange[]>([])
  const [hasExpandedAllRanges, setHasExpandedAllRanges] = useState(false)

  const addInjectedContextLines = useCallback(
    async (range: ContextLineRange) => {
      // When the diff re-renders, we don't want to scroll to the targeted
      // line or comment as we would on the initial render.
      clearUrlHash()

      expandedRangesArray.current.push(range)
      fetchContextLines({
        contextLineRanges: expandedRangesArray.current,
        url: contextLinesURL,
        path,
      })
    },
    [clearUrlHash, fetchContextLines, contextLinesURL, path],
  )

  const loadDiff = useCallback(async () => {
    await fetchContextLines({
      contextLineRanges: [],
      url: contextLinesURL,
      path,
    })
  }, [fetchContextLines, contextLinesURL, path])

  const canExpandOrCollapseLines = useMemo(() => {
    if (diff.isBinary || diff.isSubmodule || diff.isTooBig) return false

    if (
      fileRenamedOnly({
        linesChanged: diff.linesChanged,
        newTreeEntry: diff.newTreeEntry,
        oldTreeEntry: diff.oldTreeEntry,
        status: diff.status,
        truncatedReason: diff.truncatedReason,
      })
    )
      return false

    if (hasExpandedAllRanges) return true
    if (diffLines.length === 0) return false
    const firstDiffLineNumber = diffLines[1]?.blobLineNumber || 0

    if (firstDiffLineNumber > 1) return true

    const initialDiffLineCount = diffLines.length
    const lastDiffLineNumber = diffLines[initialDiffLineCount - 1]?.blobLineNumber || 0
    const fileLineCount = diff.newTreeEntry?.lineCount || 0

    //- 1 on the diff line count because there is always the top hunk diff line no matter what
    if (lastDiffLineNumber < fileLineCount || initialDiffLineCount - 1 < fileLineCount) return true

    return false
  }, [
    diff.isBinary,
    diff.isSubmodule,
    diff.isTooBig,
    diff.linesChanged,
    diff.newTreeEntry,
    diff.oldTreeEntry,
    diff.status,
    diff.truncatedReason,
    diffLines,
    hasExpandedAllRanges,
  ])

  function focusActiveElement(activeElement?: HTMLElement) {
    let cellId: string | null = null
    let filePath: string | null = null

    if (activeElement) {
      if (activeElement.hasAttribute('data-grid-cell-id')) {
        cellId = activeElement.getAttribute('data-grid-cell-id')
      }
      if (activeElement.hasAttribute('data-file-path')) {
        filePath = activeElement.getAttribute('data-file-path')
      }
    }

    setTimeout(() => {
      if (cellId) {
        const cell = document.querySelector(`[data-grid-cell-id="${cellId}"]`) as HTMLElement
        if (cell) {
          cell.focus()
          return
        }
      }
      if (filePath) {
        const button = document.querySelector(`button[data-file-path="${filePath}"]`) as HTMLElement
        if (button) {
          button.focus()
        }
      }
    }, 0)
  }

  const expandAllContextLines = async () => {
    expandedRangesArray.current = []
    if (!hasExpandedAllRanges) {
      expandedRangesArray.current.push({
        start: 1,
        end: Math.max(diff.oldTreeEntry?.lineCount ?? 0, diff.newTreeEntry?.lineCount ?? 0),
      })
    }

    const activeElement = document.activeElement as HTMLElement

    await fetchContextLines({
      contextLineRanges: expandedRangesArray.current,
      url: contextLinesURL,
      path,
    })
    setHasExpandedAllRanges(!hasExpandedAllRanges)

    if (activeElement) {
      focusActiveElement(activeElement)
    }
  }

  return (
    <Diff
      // keeping this property above the spread in order to keep a test passing, this is not
      // a change in functionality from what is currently deployed in prod
      loadDiff={loadDiff}
      // if you have any properties explicitly set above the spreading of `...diff`, and they are present on the
      // diff object itself, then the version present in the diff object will override the explicitly set version.
      // as such, I am moving the spread to the very top so that anything explicitly set will take precedence.
      {...diff}
      addFileCommentButtonRef={addFileCommentButtonRef}
      basePath={basePath}
      commentBatchPending={commentBatchPending}
      diffManuallyExpanded={diffManuallyExpanded}
      commentingImplementation={commentingImplementation}
      fileComments={fileComments}
      isAddingFileComment={isAddingFileComment}
      initialExpandedMarkerId={initialExpandedMarkerId}
      isSelected={isSelected}
      leftSideContent={
        <Suspense>
          <CodeownersBadge
            /* These classes need to match the flex ordering specified in DiffFileHeader,
      code badge currently looks strange on small screens.  */
            className="d-flex px-1 flex-items-center flex-order-2 flex-sm-order-1"
            diffPath={path}
            pullRequestBasePath={basePath}
            viewerLogin={diff.currentUser.login}
          />
        </Suspense>
      }
      rightSideContent={
        <div className="d-flex flex-items-center gap-2">
          <MarkAsViewedButton
            path={path}
            basePath={basePath}
            setIsCollapsed={value => onToggleCollapse(value, path)}
            viewed={reviewed}
          />
          {diff.currentUser.canComment && diff.commentingEnabled && (
            <IconButton
              ref={addFileCommentButtonRef}
              icon={CommentIcon}
              aria-label="Comment on this file"
              aria-expanded={isAddingFileComment}
              size="small"
              onClick={() => setIsAddingFileComment(true)}
            />
          )}
          <BlobActionsMenu
            oid={diff.status === 'REMOVED' && diff.oldCommitOid ? diff.oldCommitOid : diff.newCommitOid || ''}
            path={path}
            repo={{
              name: headRepoName || diff.repository.name,
              ownerLogin: headRepoOwner || diff.repository.ownerLogin,
            }}
            isViewable={!diff.isSubmodule}
            isDeleteable={!diff.isSubmodule}
            isEditable={!diff.isSubmodule}
            branchName={headBranchName}
            returnToPullRequestPath={basePath}
          />
        </div>
      }
      contextLinesURL={contextLinesURL}
      canExpandOrCollapseLines={canExpandOrCollapseLines}
      expandAllContextLines={expandAllContextLines}
      hasExpandedAllRanges={hasExpandedAllRanges}
      addInjectedContextLines={addInjectedContextLines}
      onToggleCollapse={(ev, value) => onToggleCollapse(value, path, ev)}
      path={path}
      ref={diffRef}
      setIsAddingFileComment={setIsAddingFileComment}
      collapsed={isCollapsed}
      helpUrl={helpUrl}
      linesAdded={diffSummary?.linesAdded || diff.linesAdded || 0}
      linesChanged={diffSummary?.linesChanged || diff.linesChanged || 0}
      linesDeleted={diffSummary?.linesDeleted || diff.linesDeleted || 0}
      diffLines={diffLinesWithThreads}
      richDiffQueryConfig={richDiffQueryConfig}
    />
  )
}

try{ PullRequestDiff.displayName ||= 'PullRequestDiff' } catch {}